import React, { useState } from 'react';
import { Table, Button, Space, Tag, message, Popconfirm, Modal, Form, InputNumber, Select, Switch } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, EyeOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCoursePricing, createCoursePricing, updateCoursePricing, deleteCoursePricing } from './api';

const { Option } = Select;

interface CoursePricing {
  id: number;
  courseId: number;
  durationDays: number;
  price: number;
  currency: string;
  isActive: boolean;
  createdAt: string;
  course: {
    id: number;
    title: string;
  };
}

interface CoursePricingTableProps {
  onEditPricing: (pricing: CoursePricing) => void;
  onCreatePricing: () => void;
}

const CoursePricingTable: React.FC<CoursePricingTableProps> = ({ onEditPricing, onCreatePricing }) => {
  const queryClient = useQueryClient();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPricing, setEditingPricing] = useState<CoursePricing | null>(null);
  const [form] = Form.useForm();

  const { data: coursePricing, isLoading } = useQuery({
    queryKey: ['coursePricing'],
    queryFn: getCoursePricing,
  });

  const createMutation = useMutation({
    mutationFn: createCoursePricing,
    onSuccess: () => {
      message.success('Цена курса успешно создана');
      queryClient.invalidateQueries({ queryKey: ['coursePricing'] });
      setIsModalVisible(false);
      form.resetFields();
    },
    onError: (error: any) => {
      message.error(error.message || 'Ошибка при создании цены курса');
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateCoursePricing,
    onSuccess: () => {
      message.success('Цена курса успешно обновлена');
      queryClient.invalidateQueries({ queryKey: ['coursePricing'] });
      setIsModalVisible(false);
      setEditingPricing(null);
      form.resetFields();
    },
    onError: (error: any) => {
      message.error(error.message || 'Ошибка при обновлении цены курса');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCoursePricing,
    onSuccess: () => {
      message.success('Цена курса успешно удалена');
      queryClient.invalidateQueries({ queryKey: ['coursePricing'] });
    },
    onError: (error: any) => {
      message.error(error.message || 'Ошибка при удалении цены курса');
    },
  });

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const getDurationText = (days: number) => {
    switch (days) {
      case 30:
        return '1 месяц';
      case 90:
        return '3 месяца';
      case 180:
        return '6 месяцев';
      default:
        return `${days} дней`;
    }
  };

  const getDurationColor = (days: number) => {
    switch (days) {
      case 30:
        return 'blue';
      case 90:
        return 'green';
      case 180:
        return 'orange';
      default:
        return 'default';
    }
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      if (editingPricing) {
        updateMutation.mutate({ id: editingPricing.id, ...values });
      } else {
        createMutation.mutate(values);
      }
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingPricing(null);
    form.resetFields();
  };

  const handleEdit = (pricing: CoursePricing) => {
    setEditingPricing(pricing);
    form.setFieldsValue({
      courseId: pricing.courseId,
      durationDays: pricing.durationDays,
      price: pricing.price,
      currency: pricing.currency,
    });
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Курс',
      dataIndex: 'course',
      key: 'course',
      render: (course: any) => (
        <div className="font-medium text-gray-900">{course.title}</div>
      ),
    },
    {
      title: 'Срок доступа',
      dataIndex: 'durationDays',
      key: 'durationDays',
      render: (days: number) => (
        <Tag color={getDurationColor(days)}>
          {getDurationText(days)}
        </Tag>
      ),
    },
    {
      title: 'Цена',
      key: 'price',
      render: (record: CoursePricing) => (
        <div className="font-medium text-green-600">
          {record.price} {record.currency}
        </div>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Активна' : 'Неактивна'}
        </Tag>
      ),
    },
    {
      title: 'Дата создания',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('ru-RU'),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      render: (record: CoursePricing) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Редактировать"
          />
          <Popconfirm
            title="Удалить цену курса?"
            description="Это действие нельзя отменить."
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Удалить"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Цены курсов</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingPricing(null);
            form.resetFields();
            setIsModalVisible(true);
          }}
          size="large"
        >
          Добавить цену
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={coursePricing}
        rowKey="id"
        loading={isLoading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} из ${total} цен`,
        }}
      />

      <Modal
        title={editingPricing ? 'Редактировать цену курса' : 'Добавить цену курса'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ currency: 'TJS' }}
        >
          <Form.Item
            name="courseId"
            label="ID курса"
            rules={[{ required: true, message: 'Введите ID курса' }]}
          >
            <InputNumber
              placeholder="Введите ID курса"
              style={{ width: '100%' }}
              min={1}
            />
          </Form.Item>

          <Form.Item
            name="durationDays"
            label="Срок доступа (дни)"
            rules={[{ required: true, message: 'Выберите срок доступа' }]}
          >
            <Select placeholder="Выберите срок доступа">
              <Option value={30}>1 месяц (30 дней)</Option>
              <Option value={90}>3 месяца (90 дней)</Option>
              <Option value={180}>6 месяцев (180 дней)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="price"
            label="Цена"
            rules={[{ required: true, message: 'Введите цену' }]}
          >
            <InputNumber
              placeholder="Введите цену"
              style={{ width: '100%' }}
              min={0}
              step={0.01}
            />
          </Form.Item>

          <Form.Item
            name="currency"
            label="Валюта"
            rules={[{ required: true, message: 'Выберите валюту' }]}
          >
            <Select placeholder="Выберите валюту">
              <Option value="TJS">TJS (сомони)</Option>
              <Option value="USD">USD (доллар)</Option>
              <Option value="EUR">EUR (евро)</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CoursePricingTable;
